import React, { useEffect, useRef, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@love-irish/ui-components/dialog"
import { useSuggestionContext } from "./context"
import type { SuggestionTarget } from "./types"
import { IconSparkles } from "@tabler/icons-react"

// Injected while picking: the custom pick cursor + the target outlines. Kept as a <style> (not a component
// class) so it can reach any [data-suggestion-target] anywhere in the document, including fields the overlay
// doesn't own. `cursorUrl` is the image; we add the hotspot + a keyword fallback so a browser that rejects
// the image (e.g. an SVG with no intrinsic size) still shows a usable cursor.
const pickStyle = (cursorUrl: string) => {
  const cursor = `url("${cursorUrl}") 5 3, auto`
  return `
  body.suggestion-picking, body.suggestion-picking * { cursor: ${cursor} !important; }
  body.suggestion-picking [data-suggestion-target] {
    cursor: ${cursor} !important;
    outline: 2px dashed rgb(168 85 247);
    outline-offset: 2px;
    border-radius: 6px;
    transition: outline-color 120ms, background-color 120ms;
  }
  body.suggestion-picking [data-suggestion-target]:hover {
    outline-style: solid;
    background-color: rgb(168 85 247 / 0.07);
  }
`
}

type Phase = "loading" | "review" | "error"

interface Picked {
  target: SuggestionTarget
  /** The field's value at pick time, if the target exposes it — shown as the "Current" side of the preview. */
  current?: string
  phase: Phase
  suggestion?: string
  error?: string
}

/**
 * The "Help me write…" pick tool. Render once inside the SuggestionTargetProvider (near the app root). When
 * pick mode is on it paints a purple cursor + outlines every registered [data-suggestion-target]; clicking
 * one runs that field's generator (the "tool" — e.g. IPA pronunciation) and opens a REVIEW MODAL that names
 * the field, previews Current → Suggested, and lets the user Regenerate, Apply, or Cancel. Apply writes the
 * value into the field's own state; the modal closes but pick mode stays armed so the next field can be
 * picked. Esc exits pick mode (or, with the modal open, closes the modal first).
 *
 * The modal is deliberately a "generation session" shell: today it is one-shot (generate → review), but the
 * marked seam below is where a refinement chat thread slots in for a future version ("make it shorter", etc.)
 * without changing the surrounding flow.
 */
export const PickOverlay: React.FC = () => {
  const ctx = useSuggestionContext()
  const { pickMode, setPickMode, getTarget, generate, cursorUrl } = ctx
  const [picked, setPicked] = useState<Picked | null>(null)
  // Guards a stale async generate from a superseded pick/regenerate.
  const pickSeq = useRef(0)

  // While the modal is open the user is focused on it, so drop the paint-bucket cursor/outlines until they
  // return to picking.
  useEffect(() => {
    const picking = pickMode && !picked
    document.body.classList.toggle("suggestion-picking", picking)
    if (!pickMode) setPicked(null)
    return () => document.body.classList.remove("suggestion-picking")
  }, [pickMode, picked])

  // Run a target's generator (shared by the initial pick and the Regenerate button).
  const runGenerate = (target: SuggestionTarget) => {
    const seq = ++pickSeq.current
    setPicked({
      target,
      current: target.capabilities.write?.getCurrentValue?.(),
      phase: "loading",
    })
    generate(target)
      .then((suggestion) => {
        if (seq === pickSeq.current)
          setPicked((p) => (p ? { ...p, phase: "review", suggestion } : p))
      })
      .catch((err: Error) => {
        if (seq === pickSeq.current)
          setPicked((p) =>
            p
              ? {
                  ...p,
                  phase: "error",
                  error: err?.message || "Couldn't generate",
                }
              : p
          )
      })
  }

  useEffect(() => {
    if (!pickMode) return

    const onClick = (event: MouseEvent) => {
      const el = (event.target as HTMLElement | null)?.closest<HTMLElement>(
        "[data-suggestion-target]"
      )
      if (!el) return
      // Intercept before the field's own handler runs.
      event.preventDefault()
      event.stopPropagation()

      const id = el.getAttribute("data-suggestion-target")
      const target = id ? getTarget(id) : undefined
      if (target) runGenerate(target)
    }
    const onKey = (event: KeyboardEvent) => {
      // Esc closes the modal first (if open), otherwise leaves pick mode. Radix also closes the open Dialog
      // on Esc via onOpenChange, so here we only handle the "no modal" case.
      if (event.key === "Escape" && !picked) setPickMode(false)
    }

    document.addEventListener("click", onClick, true) // capture phase
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("click", onClick, true)
      document.removeEventListener("keydown", onKey)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickMode, getTarget, generate, setPickMode, picked])

  if (!pickMode) return null

  const closeModal = () => setPicked(null)
  const apply = () => {
    if (picked?.suggestion != null)
      picked.target.capabilities.write?.apply(picked.suggestion)
    setPicked(null)
  }

  const hasCurrent = picked?.current != null && picked.current.trim() !== ""

  return (
    <>
      <style>{pickStyle(cursorUrl)}</style>

      {/* A slim banner so it's obvious the tool is armed + how to leave. */}
      <div
        className="fixed inset-x-0 top-0 z-[60] flex items-center justify-center gap-3 bg-suggestion-600 px-4 py-3 text-sm text-white animate-slide-in-top"
        data-testid="pick-banner"
      >
        <span className="flex items-center gap-2">
          <IconSparkles /> <strong>Help me write</strong> — selected a
          highlighted field to generate content
        </span>
        <button
          type="button"
          onClick={() => setPickMode(false)}
          className="rounded bg-suggestion-500 px-2 py-0.5 text-xs font-medium hover:bg-suggestion-400"
        >
          Done (Esc)
        </button>
      </div>

      <Dialog
        open={!!picked}
        onOpenChange={(open) => {
          if (!open) closeModal()
        }}
      >
        {picked && (
          <DialogContent className="max-w-2xl" data-testid="pick-review">
            <DialogHeader>
              {/* The field we're generating for — named so it's clear what Apply will change. */}
              <DialogTitle className="flex items-center gap-2">
                <span className="text-suggestion-500" aria-hidden>
                  ✦
                </span>
                {picked.target.label}
              </DialogTitle>
              <DialogDescription>
                Help me write. Review the draft, then apply it to the{" "}
                <span className="font-medium">{picked.target.label}</span>{" "}
                field.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {hasCurrent && (
                <div>
                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Current
                  </p>
                  <div className="max-h-32 overflow-auto whitespace-pre-wrap break-words rounded-md border border-gray-200 bg-gray-50 p-2.5 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
                    {picked.current}
                  </div>
                </div>
              )}

              {picked.phase === "loading" && (
                <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  Generating…
                </p>
              )}
              {picked.phase === "error" && (
                <p className="text-sm text-danger-600">{picked.error}</p>
              )}
              {picked.phase === "review" && (
                <div>
                  <p className="mb-1 flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-suggestion-700 dark:text-suggestion-300">
                    ✦ {hasCurrent ? "Suggested replacement" : "Suggested"}
                  </p>
                  <div className="max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-md border border-suggestion-300 bg-suggestion-50 p-2.5 text-sm text-suggestion-900 dark:border-suggestion-500 dark:bg-suggestion-950/40 dark:text-suggestion-100">
                    {picked.suggestion || "(empty)"}
                  </div>
                </div>
              )}

              {/* FUTURE (chat refinement): a message list + input slots in right here. The first generate is
                  turn 1; the user types "make it shorter / more formal" and we re-run generate() with the
                  accumulated thread, updating `picked.suggestion`. The rest of this flow is unchanged. */}
            </div>

            <DialogFooter className="sm:justify-between">
              <button
                type="button"
                onClick={() => runGenerate(picked.target)}
                disabled={picked.phase === "loading"}
                className="text-sm font-medium text-suggestion-600 hover:text-suggestion-700 disabled:cursor-not-allowed disabled:text-gray-400 dark:text-suggestion-400 dark:hover:text-suggestion-300"
              >
                ↻ Regenerate
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={apply}
                  disabled={picked.phase !== "review"}
                  className="rounded-md bg-suggestion-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-suggestion-700 disabled:cursor-not-allowed disabled:bg-gray-300 dark:disabled:bg-gray-600"
                >
                  Apply
                </button>
              </div>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </>
  )
}

export default PickOverlay
